import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { GridCellProps } from '@progress/kendo-react-grid';
import { FC, useCallback, useMemo } from 'react';
import CrossCircleFileIcon from 'src/assets/icons/CrossCircleFileIcon';
import DollarCircleFileIcon from 'src/assets/icons/DollarCircleFileIcon';
import FilesIcon from 'src/assets/icons/FilesIcon';
import QuesCircleFileIcon from 'src/assets/icons/QuesCircleFileIcon';
import WarningCircleFileIcon from 'src/assets/icons/WarningCircleFileIcon';
import WarningTriFileIcon from 'src/assets/icons/WarningTriFileIcon';
import GridCellBase from 'src/components/GridCellBase';
import PopUp from 'src/components/design-system/PopUp';
import useActionRequiredInvoices from 'src/hooks/useActionRequiredInvoices';
import { initialActionRequiredDataState } from '../ActionRequiredInvoicesContext';

interface ToolTipContentProps {
  content: string;
  reasonContent: boolean;
}

const ToolTipContent: FC<ToolTipContentProps> = ({ content, reasonContent }) => (
  <div style={{ fontSize: '0.6rem' }}>
    {content}
    {reasonContent && (
      <div className="flex items-center" style={{ color: '#D9534F' }}>
        <div className="k-icon k-i-warning error mr-1" style={{ fontSize: '0.7rem' }} />
        Reason why invoice was sent for approval
      </div>
    )}
  </div>
);

const StatusFilesField: FC<GridCellProps> = (props) => {
  const { setActionRequiredDataState, setAdditionalGLCodeFilter, setSelectedGLSummaryRowId } =
    useActionRequiredInvoices();
  const { dataItem } = props;
  const {
    requesterInvoiceOverCreatorLimit = false,
    requesterInvoiceGlMismatch = false,
    requesterInvoiceContainsNewLineItem = false,
    requesterInvoiceItemQtyOverage = false,
    requesterInvoiceItemPriceOverage = false,
    requesterInvoiceOverPoDollarLimit = false,
    requesterInvoiceOverPoPercentLimit = false,
    invoiceOverCreatorLimit = false,
    invoiceGlMismatch = false,
    invoiceContainsNewLineItem = false,
    invoiceItemQtyOverage = false,
    invoiceItemPriceOverage = false,
    invoiceOverPoDollarLimit = false,
    invoiceOverPoPercentLimit = false,
  } = dataItem;

  const handleStatusFilter = useCallback(
    (filterType: string) => {
      setAdditionalGLCodeFilter('');
      setSelectedGLSummaryRowId('');
      setActionRequiredDataState((prev) => {
        let newState: State = { ...prev };
        let newFilter: CompositeFilterDescriptor = {
          logic: 'and',
          filters: [{ field: filterType, value: 'true', operator: 'eq' }],
        };
        if (!newState?.filter) return initialActionRequiredDataState;
        newState.filter.filters = [...newState.filter.filters, newFilter];
        return newState;
      });
    },
    [setActionRequiredDataState, setAdditionalGLCodeFilter, setSelectedGLSummaryRowId]
  );

  const invoiceOverCreatorLimitColor = useMemo(() => {
    let color = '#797474';
    if (requesterInvoiceOverCreatorLimit) {
      color = '#D9534F';
    } else if (invoiceOverCreatorLimit) {
      color = '#b47a08';
    }
    return color;
  }, [invoiceOverCreatorLimit, requesterInvoiceOverCreatorLimit]);

  const invoiceGlMismatchColor = useMemo(() => {
    let color = '#AAAAAA';
    if (requesterInvoiceGlMismatch) {
      color = '#D9534F';
    } else if (invoiceGlMismatch) {
      color = '#D9AA4F';
    }
    return color;
  }, [invoiceGlMismatch, requesterInvoiceGlMismatch]);

  const invoiceContainsNewLineItemColor = useMemo(() => {
    let color = '#797474';
    if (requesterInvoiceContainsNewLineItem) {
      color = '#D9534F';
    } else if (invoiceContainsNewLineItem) {
      color = '#b47a08';
    }
    return color;
  }, [invoiceContainsNewLineItem, requesterInvoiceContainsNewLineItem]);

  const invoiceItemQtyOverageColor = useMemo(() => {
    let color = '#797474';
    if (requesterInvoiceItemQtyOverage) {
      color = '#D9534F';
    } else if (invoiceItemQtyOverage) {
      color = '#b47a08';
    }
    return color;
  }, [invoiceItemQtyOverage, requesterInvoiceItemQtyOverage]);

  const invoiceItemPriceOverageColor = useMemo(() => {
    let color = '#797474';
    if (requesterInvoiceItemPriceOverage) {
      color = '#D9534F';
    } else if (invoiceItemPriceOverage) {
      color = '#b47a08';
    }
    return color;
  }, [invoiceItemPriceOverage, requesterInvoiceItemPriceOverage]);

  const invoiceOverPoPercentLimitColor = useMemo(() => {
    let color = '#797474';
    if (requesterInvoiceOverPoPercentLimit) {
      color = '#D9534F';
    } else if (invoiceOverPoPercentLimit) {
      color = '#b47a08';
    }
    return color;
  }, [invoiceOverPoPercentLimit, requesterInvoiceOverPoPercentLimit]);

  const invoiceOverPoDollarLimitColor = useMemo(() => {
    let color = '#797474';
    if (requesterInvoiceOverPoDollarLimit) {
      color = '#D9534F';
    } else if (invoiceOverPoDollarLimit) {
      color = '#b47a08';
    }
    return color;
  }, [invoiceOverPoDollarLimit, requesterInvoiceOverPoDollarLimit]);

  return (
    <GridCellBase {...props} disableToolTip style={{ overflow: 'visible' }}>
      <div id="status-files-field" className="flex justify-between w-full overflow-visible-imp">
        <PopUp
          content={
            <div className="m-1 max-w-[350px] p-2">
              <ToolTipContent
                content="Invoice value over creator's invoice $ limit"
                reasonContent={requesterInvoiceOverCreatorLimit}
              />
            </div>
          }
        >
          <button
            className="cursor-pointer"
            disabled={!requesterInvoiceOverCreatorLimit}
            onClick={() => handleStatusFilter('requesterInvoiceOverCreatorLimit')}
          >
            <WarningCircleFileIcon color={invoiceOverCreatorLimitColor} />
          </button>
        </PopUp>
        <PopUp
          content={
            <div className="m-1 max-w-[350px] p-2">
              <ToolTipContent
                content="Invoice contains changes to GL codes"
                reasonContent={requesterInvoiceGlMismatch}
              />
            </div>
          }
        >
          <button
            className="cursor-pointer w-fit"
            disabled={!requesterInvoiceGlMismatch}
            onClick={() => handleStatusFilter('requesterInvoiceGlMismatch')}
          >
            <WarningTriFileIcon color={invoiceGlMismatchColor} />
          </button>
        </PopUp>
        <PopUp
          content={
            <div className="m-1 max-w-[350px] p-2">
              <ToolTipContent
                content="Invoice contains appended items"
                reasonContent={requesterInvoiceContainsNewLineItem}
              />
            </div>
          }
        >
          <button
            className="cursor-pointer w-fit"
            disabled={!requesterInvoiceContainsNewLineItem}
            onClick={() => handleStatusFilter('requesterInvoiceContainsNewLineItem')}
          >
            <FilesIcon color={invoiceContainsNewLineItemColor} />
          </button>
        </PopUp>
        <PopUp
          content={
            <div className="m-1 max-w-[350px] p-2">
              <ToolTipContent
                content="Invoice contains item quantities > PO quantities"
                reasonContent={requesterInvoiceItemQtyOverage}
              />
            </div>
          }
        >
          <button
            className="cursor-pointer w-fit"
            disabled={!requesterInvoiceItemQtyOverage}
            onClick={() => handleStatusFilter('requesterInvoiceItemQtyOverage')}
          >
            <CrossCircleFileIcon color={invoiceItemQtyOverageColor} />
          </button>
        </PopUp>
        <PopUp
          content={
            <div className="m-1 max-w-[350px] p-2">
              <ToolTipContent
                content="Invoice contains price $ > PO $"
                reasonContent={requesterInvoiceItemPriceOverage}
              />
            </div>
          }
        >
          <button
            className="cursor-pointer w-fit"
            disabled={!requesterInvoiceItemPriceOverage}
            onClick={() => handleStatusFilter('requesterInvoiceItemPriceOverage')}
          >
            <DollarCircleFileIcon color={invoiceItemPriceOverageColor} />
          </button>
        </PopUp>
        <PopUp
          content={
            <div className="m-1 max-w-[350px] p-2">
              <ToolTipContent
                content="Invoice contains total that is > pre-set % of the PO value"
                reasonContent={requesterInvoiceOverPoPercentLimit}
              />
            </div>
          }
        >
          <button
            className="cursor-pointer w-fit"
            disabled={!requesterInvoiceOverPoPercentLimit}
            onClick={() => handleStatusFilter('requesterInvoiceOverPoPercentLimit')}
          >
            <QuesCircleFileIcon color={invoiceOverPoPercentLimitColor} />
          </button>
        </PopUp>
        <PopUp
          content={
            <div className="m-1 max-w-[350px] p-2">
              <ToolTipContent
                content="Invoice dollar over PO invoice $ limit"
                reasonContent={requesterInvoiceOverPoDollarLimit}
              />
            </div>
          }
        >
          <button
            className="cursor-pointer w-fit"
            disabled={!requesterInvoiceOverPoDollarLimit}
            onClick={() => handleStatusFilter('requesterInvoiceOverPoDollarLimit')}
          >
            <DollarCircleFileIcon color={invoiceOverPoDollarLimitColor} />
          </button>
        </PopUp>
      </div>
    </GridCellBase>
  );
};

export default StatusFilesField;
